<script setup lang="ts">
import { ref, reactive, defineEmits } from 'vue';
import { useRoute } from 'vue-router';
import AxiosAudioService from "../utils/axiosAudioService";
import { useLanguage } from "../utils/useLanguage";

const { t } = useLanguage();

// ================ Recording ================
// Constants
const AUDIO_MIME_TYPE = "audio/mp4"; // recording file
const AUDIO_BLOB_TYPE = 'audio/mp3'; // final recording file format
const RECORDING_TITLE = 'New Recording';

// Axios Service
const axiosAudioService = new AxiosAudioService();
const route = useRoute();
const articleId: string = route.params.id as string;


// Recording State
const hasUnsavedRecording = ref(false);
const recordingState = ref({
  isRecording: false,
  isPaused: false,
  audioFile: null as Blob | null,
  mediaRecorder: null as MediaRecorder | null,
  audioChunks: [] as BlobPart[],
  mediaStream: null as MediaStream | null,
  timeElapsed: 0,
  timerInterval: undefined as number | undefined,
  audioURL: null as string | null,  // Store audio URL here
});

// Start Recording
const startRecording = async () => {

  recordingState.value.audioURL = null
  try {
    recordingState.value.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    resetRecordingState();
    
    const options = { mimeType: AUDIO_MIME_TYPE };
    recordingState.value.mediaRecorder = new MediaRecorder(recordingState.value.mediaStream, options);
    
    recordingState.value.mediaRecorder.ondataavailable = (event) => {
      recordingState.value.audioChunks.push(event.data);
    };

    recordingState.value.mediaRecorder.onstop = handleRecordingStop;

    recordingState.value.mediaRecorder.start();
    recordingState.value.isRecording = true;
    hasUnsavedRecording.value = false;
    startTimer();
  } catch (error) {
    handleError('Error accessing media devices:', error);
  }
};

// Stop Recording
const stopRecording = () => {
  if (recordingState.value.mediaRecorder) {
    recordingState.value.mediaRecorder.stop();
    recordingState.value.isRecording = false;
    hasUnsavedRecording.value = true;
    recordingState.value.isPaused = false;
    stopTimer();
  }
};

// Pause/Resume Recording
const togglePauseRecording = () => {
  if (recordingState.value.mediaRecorder) {
    if (recordingState.value.isPaused) {
      recordingState.value.mediaRecorder.resume();
      startTimer();
    } else {
      recordingState.value.mediaRecorder.pause();
      stopTimer();
    }
    recordingState.value.isPaused = !recordingState.value.isPaused;
  }
};

// Handle recording stop
const handleRecordingStop = () => {
  const audioBlob = new Blob(recordingState.value.audioChunks, { type: AUDIO_BLOB_TYPE });
  recordingState.value.audioFile = audioBlob;
  recordingState.value.audioChunks = []; // Clear recorded chunks
  recordingState.value.audioURL = URL.createObjectURL(audioBlob); // Create and store audio URL
  stopTimer();
  stopMediaStream();
};

// Save Recording
const isSaving = ref(false)
const modalMessage = ref("Saving...");

const saveRecording = async () => {
  if (!recordingState.value.audioFile) {
    console.error("No audio file to save.");
    return;
  }

  isSaving.value = true;
  hasUnsavedRecording.value = false;
  openSaveModal(); // Show modal immediately with "Saving..."

  const formData = new FormData();
  formData.append("audio_file", recordingState.value.audioFile);
  formData.append("title", RECORDING_TITLE);
  formData.append("article_id", articleId);

  try {
    const res = await axiosAudioService.createAudio(localStorage.token, formData, "multipart/form-data");
    if (res.status === 200) {
      modalMessage.value = "Success! Your recording has been saved.";
      console.log("Audio recording saved successfully:", res);
    } else {
      modalMessage.value = "Failed to save the recording.";
      console.error("Failed to save audio recording:", res);
    }
  } catch (error: any) {
    if (error.response?.status === 401) {
      modalMessage.value = "Unauthorized! Please log in again.";
      console.error("401 Unauthorized error:", error);
      window.location.href = "/login"; // Redirect to login page
    } else {
      modalMessage.value = "Error saving the recording.";
      console.error("Error saving audio recording:", error);
    }
  } finally {
    isSaving.value = false;
  }
};

function openSaveModal() {
  modalMessage.value = "Saving..."; // Reset to "Saving..."
  openSave.value = true;
  modalSetting.status = true;
}

function closeSaveModal() {
  openSave.value = false;
  modalSetting.status = false;
  emit("close-recording-tool");
}

// ================ Save confirm Modal================

const emit = defineEmits(['close-recording-tool']);
const modalSetting = reactive({
  status: false, // Modal Open or Close
})

let openSave = ref(false)

// ================ Timer Management ================

const startTimer = () => {
  recordingState.value.timerInterval = window.setInterval(() => {
    recordingState.value.timeElapsed += 1;
  }, 1000);
};

const stopTimer = () => {
  if (recordingState.value.timerInterval !== undefined) {
    clearInterval(recordingState.value.timerInterval);
    recordingState.value.timerInterval = undefined;
  }
};

// Format time as mm:ss
const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Helper Functions
const resetRecordingState = () => {
  recordingState.value.timeElapsed = 0;
  recordingState.value.audioChunks = [];
};

const stopMediaStream = () => {
  recordingState.value.mediaStream?.getTracks().forEach(track => track.stop());
};

const handleError = (message: string, error: any) => {
  console.error(message, error);
  if (error.response) {
    console.log('Response data:', error.response.data);
    console.log('Response status:', error.response.status);
  }
};
</script>
<template>
  <div
    class="audio-recorder"
    :class="{ 'recording-active': recordingState.isRecording }"
  >
    <!-- Time Display -->
    <span class="white-1 ml-20 mr-20"> 
      {{ t('New Recording') }} {{ formatTime(recordingState.timeElapsed) }}
    </span>

    <!-- Custom audio player -->
    <audio v-if="recordingState.audioURL" :src="recordingState.audioURL" class="mr-20" controls></audio>
    
    <!-- Controls -->
    <div class="controls">
      <button v-if="recordingState.isRecording" class="flex a-center" @click="togglePauseRecording">
        <i class="white-1 md pointer" :class="recordingState.isPaused ? 'icon-play-filled' : 'icon-pause-filled'" alt="Pause/Resume Icon"></i>
      </button>

      <button v-if="!recordingState.isRecording" class="flex a-center" @click="startRecording">
        <i class="dark icon-record md pointer mr-20" alt="Record Icon"></i>
      </button>

      <button v-if="recordingState.isRecording" class="flex a-center" @click="stopRecording">
        <i class="white-1 icon-stop-filled md pointer mr-20" alt="Stop Icon"></i>
      </button>

      <button v-if="hasUnsavedRecording" class="btn br-40 bg-pr flex a-center white-1 mr-20" @click="saveRecording">
        {{ t('Save') }}
      </button>
    </div>

    <!-- Audio Save Successfully Modal -->
    <div id="saveSuccess" class="modal fixed p-center" :class="{ active: openSave }">
      <div class="modalTitle title-1 flex j-between bg-dark white-1">
        {{ modalMessage === "Saving..." ? t('Saving...') : t('Save Successfully!') }}
        <div class="icon-close pointer white-1" @click="closeSaveModal"></div>
      </div>
      <div class="modalContent bg-white-1 scroll">
        <p class="fw-bold">{{ t(modalMessage) }}</p>
        <p v-if="modalMessage !== 'Saving...'" class="mb-20">
          {{ t('You can now head over to My Recording to view your saved audio. Thank you for using our recording feature!') }}
        </p>
      </div>
      <div class="modalFooter bg-white-1">
        <div class="flex gap-5 j-center">
          <router-link v-if="modalMessage !== 'Saving...'" class="btn-sm bg-dark" to="/user/record">{{ t('Check My Recording') }}</router-link>
        </div>
      </div>
    </div>
  </div>

  <div id="mask" class="mask fixed" :class="modalSetting.status ? 'active': ''"></div>
</template>



<style scoped>
@import '../assets/css_lib/themimir.css';

/* Audio Recorder Styles */
.audio-recorder {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #19231A;
  color: white;
  padding: 10px;
  max-width: 50%;
  height: 70px;
  border-radius: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
}

.audio-recorder.recording-active {
  background-color: #D81E5B; /* 錄音進行時的顏色 */
}

/* Audio Player */
.audio-recorder audio {
  flex-grow: 1;
  max-width: 400px;
  background-color: #19231A;
}

/* Controls for the Record Button */
.controls {
  display: flex;
  align-items: center;
}

.controls .record-btn {
  display: flex;
  align-items: center;
  background-color: #ff4f4f;
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: 50px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.controls .record-btn:hover {
  background-color: #e33d3d;
}

.controls img {
  width: 20px;
  height: 20px;
  margin-right: 10px;
}

.modal.active{
  top: -40vh;
}

.modal.active {
  display: block; /* Ensure the modal is displayed when active */
  z-index: 1001;
}

.mask.active {
  display: block; /* Ensure the mask is displayed when modal is active */
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 1; 
}
</style>
